
/**
 * PIVOT / Focal / Globo Product Options
 *
 * Features:
 * 1. Open Focal cart drawer after Globo adds products.
 * 2. Remove linked accessories with the main product.
 * 3. Synchronize accessory descriptions with Shopify cart.
 *
 * Supports:
 * + Add Ons
 * + Adds On
 * + Add On
 * + Clip Kit
 * + Bottle Opener Keychain
 * +
 * + PPK Mounting Plate
 *
 * Legacy:
 * ++
 * Colored +
 *
 * Additional linked accessory fields can be recognized
 * automatically from Globo's _gpo_field_name.
 */


/* =========================================================
   1. GLOBO -> FOCAL CART DRAWER
   ========================================================= */

(function () {
  if (!window.fetch || window.__globoFocalCartInstalled) return;

  window.__globoFocalCartInstalled = true;

  const originalFetch = window.fetch;
  let refreshTimer;

  window.fetch = async function (...args) {
    const response = await originalFetch.apply(this, args);

    const url = typeof args[0] === 'string'
      ? args[0]
      : args[0]?.url || '';

    if (
      response.ok &&
      /\/cart\/add(?:\.js)?(?:\?|$)/.test(url) &&
      window.location.pathname.startsWith('/products/')
    ) {
      clearTimeout(refreshTimer);

      refreshTimer = setTimeout(async () => {
        try {
          const cartResponse = await originalFetch('/cart.js');

          if (!cartResponse.ok) return;

          const cart = await cartResponse.json();

          document.documentElement.dispatchEvent(
            new CustomEvent('cart:refresh', {
              bubbles: true,
              detail: {
                cart,
                openMiniCart: true
              }
            })
          );

        } catch (error) {
          console.warn(
            'Unable to refresh Focal cart:',
            error
          );
        }
      }, 500);
    }

    return response;
  };
})();


/* =========================================================
   2. REMOVE LINKED ACCESSORIES WITH MAIN PRODUCT
   ========================================================= */

(function () {
  if (window.__gpoBundleRemovalInstalled) return;

  window.__gpoBundleRemovalInstalled = true;

  document.addEventListener('click', async function (event) {
    const button = event.target.closest(
      '.line-item__remove-button[data-gpo-group]'
    );

    if (!button) return;

    const group = button.dataset.gpoGroup;

    if (!group) return;

    event.preventDefault();
    event.stopPropagation();

    if (button.dataset.gpoRemoving === 'true') return;

    button.dataset.gpoRemoving = 'true';

    try {
      const cartResponse = await fetch('/cart.js');

      if (!cartResponse.ok) {
        throw new Error('Cart lookup failed');
      }

      const cart = await cartResponse.json();

      const parent = cart.items.find(item =>
        item.properties?._gpo_product_group === group
      );

      if (!parent) {
        throw new Error('Main product not found');
      }

      const children = cart.items.filter(item =>
        item.properties?._gpo_parent_product_group === group
      );

      const updates = {};

      updates[parent.key] = 0;

      children.forEach(item => {
        updates[item.key] = 0;
      });

      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ updates })
      });

      if (!response.ok) {
        throw new Error('Bundle removal failed');
      }

      const updatedCart = await response.json();

      document.documentElement.dispatchEvent(
        new CustomEvent('cart:refresh', {
          bubbles: true,
          detail: {
            cart: updatedCart,
            openMiniCart: true
          }
        })
      );

    } catch (error) {
      console.error(
        'Globo bundle removal error:',
        error
      );

      alert('Unable to remove bundle. Please try again.');

    } finally {
      delete button.dataset.gpoRemoving;
    }
  }, true);
})();


/* =========================================================
   3. SYNCHRONIZE GLOBO ACCESSORIES WITH SHOPIFY CART
   ========================================================= */

(function () {
  if (!window.fetch || window.__gpoCheckoutSyncInstalled) return;

  window.__gpoCheckoutSyncInstalled = true;

  const previousFetch = window.fetch.bind(window);

  const root = window.Shopify?.routes?.root || '/';
  const path = suffix => root + suffix;

  const addonFields = [
    '+ Add Ons',
    '+ Adds On',
    '+ Add On'
  ];

  const clipField = '+ Clip Kit';
  const bottleField = '+ Bottle Opener Keychain';

  const mountingPlateFields = [
    '+',
    '+ PPK Mounting Plate',
    '++',
    'Colored +'
  ];

  const knownFields = [
    ...addonFields,
    clipField,
    bottleField,
    ...mountingPlateFields
  ];

  let timer;
  let running = null;
  let pending = false;

  function hasProperty(object, key) {
    return Object.prototype.hasOwnProperty.call(
      object,
      key
    );
  }

  function normalize(value) {
    return String(value || '')
      .toLowerCase()
      .replace(/\s+-\s+bundle$/i, '')
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();
  }

  function oldLabels(value) {
    return String(value || '')
      .split(',')
      .map(value => value.trim())
      .filter(Boolean);
  }

  function childLabel(child, labels) {
    const base = String(
      child.product_title || child.title || ''
    )
      .replace(/\s+-\s+bundle$/i, '')
      .trim();

    const match = labels.find(label => {
      const a = normalize(label);
      const b = normalize(base);

      return a && b && (
        a === b ||
        a.startsWith(b + ' ') ||
        b.startsWith(a + ' ')
      );
    });

    return match || base;
  }

  function variantLabel(child) {
    const variant = String(
      child.variant_title || ''
    ).trim();

    if (
      variant &&
      variant !== 'Default Title'
    ) {
      return variant;
    }

    return String(child.title || '')
      .replace(/\s+-\s+bundle$/i, '')
      .trim();
  }

  function mountingPlateLabel(child, labels) {
    const variant = String(
      child.variant_title || ''
    ).trim();

    if (
      variant &&
      variant !== 'Default Title'
    ) {
      const match = labels.find(label =>
        normalize(label).includes(
          normalize(variant)
        )
      );

      if (match) return match;
    }

    if (labels.length === 1) {
      return labels[0];
    }

    const base = String(
      child.product_title || child.title || ''
    )
      .replace(/\s+-\s+bundle$/i, '')
      .trim();

    if (
      variant &&
      variant !== 'Default Title'
    ) {
      return base + ' - ' + variant;
    }

    return base;
  }

  function accessoryValue(field, children, previousValue) {
    const labels = oldLabels(previousValue);

    if (addonFields.includes(field)) {
      return [...new Set(
        children.map(child =>
          childLabel(child, labels)
        )
      )].join(', ');
    }

    if (field === clipField || field === bottleField) {
      return [...new Set(
        children.map(variantLabel)
          .filter(Boolean)
      )].join(', ');
    }

    if (mountingPlateFields.includes(field)) {
      return [...new Set(
        children.map(child =>
          mountingPlateLabel(child, labels)
        )
      )].join(', ');
    }

    // For newly discovered Globo linked accessory fields,
    // preserve the existing label when it matches a child.
    if (children.length === 1 && previousValue) {
      return previousValue;
    }

    return [...new Set(
      children.map(variantLabel)
        .filter(Boolean)
    )].join(', ');
  }

  async function readCart() {
    const response = await previousFetch(
      path('cart.js'),
      { cache: 'no-store' }
    );

    if (!response.ok) {
      throw new Error('Could not read Shopify cart');
    }

    return response.json();
  }

  async function synchronizeOnce() {
    let cart = await readCart();
    let changed = false;

    const groups = [...new Set(
      cart.items
        .filter(item =>
          item.properties?._gpo_product_group
        )
        .map(item =>
          item.properties._gpo_product_group
        )
    )];

    for (const group of groups) {
      const parent = cart.items.find(item =>
        item.properties?._gpo_product_group === group
      );

      if (!parent) continue;

      const properties = {
        ...parent.properties
      };

      const children = cart.items.filter(item =>
        item.properties?._gpo_parent_product_group === group
      );

      // Recognize fields explicitly supported by the code,
      // plus linked accessory fields present in this cart.
      const linkedFields = children
        .map(item => item.properties?._gpo_field_name)
        .filter(Boolean);

      const supportedFields = [...new Set([
        ...knownFields,
        ...linkedFields
      ])];

      let needsUpdate = false;

      for (const field of supportedFields) {
        const fieldChildren = children.filter(item =>
          item.properties?._gpo_field_name === field
        );

        const exists = hasProperty(
          properties,
          field
        );

        if (
          !exists &&
          fieldChildren.length === 0
        ) {
          continue;
        }

        const previousValue = String(
          properties[field] || ''
        );

        const newValue = accessoryValue(
          field,
          fieldChildren,
          previousValue
        );

        if (newValue !== previousValue) {
          if (newValue) {
            properties[field] = newValue;
          } else {
            delete properties[field];
          }

          needsUpdate = true;
        }
      }

      if (!needsUpdate) continue;

      const response = await previousFetch(
        path('cart/change.js'),
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            id: parent.key,
            quantity: parent.quantity,
            properties
          })
        }
      );

      if (!response.ok) {
        console.warn(
          'Globo sync response:',
          response.status,
          await response.text()
        );

        throw new Error(
          'Could not update parent product properties'
        );
      }

      cart = await response.json();
      changed = true;
    }

    if (changed) {
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:refresh', {
          bubbles: true,
          detail: {
            cart,
            openMiniCart: false
          }
        })
      );
    }

    return changed;
  }

  async function synchronize() {
    if (running) {
      pending = true;
      return running;
    }

    running = synchronizeOnce();

    try {
      return await running;

    } catch (error) {
      console.warn(
        'Globo checkout sync error:',
        error
      );

      return false;

    } finally {
      running = null;

      if (pending) {
        pending = false;
        schedule();
      }
    }
  }

  function schedule() {
    clearTimeout(timer);

    timer = setTimeout(
      synchronize,
      600
    );
  }

  window.fetch = async function (...args) {
    const response = await previousFetch(...args);

    const url = typeof args[0] === 'string'
      ? args[0]
      : args[0]?.url || '';

    if (
      response.ok &&
      /\/cart\/(?:add|change|update|clear)(?:\.js)?(?:\?|$)/.test(url)
    ) {
      schedule();
    }

    return response;
  };

  document.addEventListener(
    'cart:updated',
    schedule
  );

  document.documentElement.addEventListener(
    'cart:refresh',
    schedule
  );

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      schedule,
      { once: true }
    );
  } else {
    schedule();
  }
})();
